<script setup>
import { gameTypeLabel } from '../services/game-label'
/**
 * 牌九「数据统计」弹窗，逐条对齐旧版 web/19.0d8589924b85d555a0a0.js 里的
 * app-statistic 组件（类 d、模板工厂 Vl、样式 Pl）。
 *
 * 旧版调用点有两处：
 *   statisticModal(true)  → componentProps.game_no = -1   开奖动画结束前自动弹出
 *   statisticModal(false) → componentProps.game_no = 当期  右侧菜单「统计」手动弹出
 * 后端 Game::statistic() 对 game_no == -1（或 'last'）会取最近一期已开奖的期号，
 * 所以 -1 等价于「上一期报表」。
 *
 * 旧版用 Ionic 的 ion-list / ion-item / ion-refresher / ion-infinite-scroll，
 * 这里没有 Ionic，改用同名 class 的普通元素承载，CSS 按旧版 Pl 逐条还原。
 */
import { computed, onMounted, ref } from 'vue'
import { apiConfig, request } from '../services/api'

const props = defineProps({
  // 旧版 navParams.get('game')：完整 game 对象，请求只用到 game_id 与 game_state.game_no
  game: { type: Object, required: true },
  // 旧版 navParams.get('game_no')
  gameNo: { type: [String, Number], default: -1 },
})
const emit = defineEmits(['close'])

// 旧版 statistic = {total:new Array, lists:new Array, member:new Array, member_total:0}
const statistic = ref({ total: [], lists: [], member: [], member_total: 0 })
// 旧版 lotteryState：getLotteryData 成功才为 true，决定表头文案与中奖/盈利列是否显示数字
const lotteryState = ref(false)
const lotteryGameNo = ref(0)
// 旧版 lotteryData = data.format_lottery_numbers，模板只用到 lotteryData.result
const lotteryData = ref(null)
// 后端 Api::page() 的结构：{item_total, page_index, page_size, page_count}
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 1 })
const loading = ref(false)
const errorMessage = ref('')

// 旧版构造函数里是 `game_no && (this.game.game_state.game_no = this.game_no)`，
// 之后所有请求都读 game.game_state.game_no。那样会改到父组件传进来的对象，
// 这里改成本地派生值，行为一致但没有副作用。
const gameId = computed(() => props.game?.game_id)
const isOzbPj = computed(() => String(props.game?.game_name || '').toLowerCase() === 'ozbpj')
const requestGameNo = computed(() => (props.gameNo || props.game?.game_state?.game_no || ''))
// 旧版 !lotteryState 时表头期号读 game.game_state.game_no，lotteryState 时读 lotteryGameNo
const headerGameNo = computed(() => (lotteryState.value ? lotteryGameNo.value : requestGameNo.value))
const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

// 旧版模板用 Angular DecimalPipe。'.2-2' = 固定两位小数；两者都带千位分隔符
// （Angular 默认 locale 是 en-US），所以这里用 en-US 的 Intl.NumberFormat 复刻。
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }
// 旧版 table 里 total / member 两行的「中奖」「盈利」列没有套 pipe，直接输出原始值
function raw(value) { return value }
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

// 头像相对路径挂在后端站点根上，从已配置的 apiServer 推导源站，避免写死域名。
const assetOrigin = (() => {
  try { return new URL(apiConfig.apiServer).origin } catch (_) { return '' }
})()
/** 旧版 API 内置头像值是 /assets/userface.png，本地没有该文件，回退到打包的默认头像 */
function avatarUrl(value) {
  if (!value || value === '/assets/userface.png') return '/legacy/images/userface-default.png'
  if (/^(?:https?:|data:|blob:)/i.test(value)) return value
  return `${assetOrigin}${value.startsWith('/') ? value : `/${value}`}`
}
function onAvatarError(event) { event.target.onerror = null; event.target.src = '/legacy/images/userface-default.png' }

/** 旧版 getStatistic(l=null)：page_index===1 时整体覆盖，否则只追加 lists */
async function loadStatistic() {
  if (!gameId.value || !requestGameNo.value) return
  const pageIndex = page.value.page_index
  const response = await request('/game/statistic', {
    method: 'POST',
    data: {
      game: gameId.value,
      game_no: requestGameNo.value,
      // 旧版第三个参数是 `1 === this.page.page_index`，即只有第一页才要 member 段
      member: pageIndex === 1,
      page: pageIndex,
      pagesize: page.value.page_size,
    },
  })
  page.value = { ...page.value, ...(response.page || {}) }
  const data = response.data || {}
  if (pageIndex === 1) {
    statistic.value = {
      total: data.total || [],
      member: data.member || [],
      lists: data.lists || [],
      member_total: data.member_total || 0,
    }
  } else {
    statistic.value = { ...statistic.value, lists: [...statistic.value.lists, ...(data.lists || [])] }
  }
}

/** 旧版 getLotteryData()：拿 format_lottery_numbers，失败时 lotteryState=false */
async function loadLottery() {
  if (!gameId.value || !requestGameNo.value) return
  try {
    const response = await request('/game/history', {
      method: 'POST',
      data: { game: gameId.value, game_no: requestGameNo.value },
    })
    const row = Array.isArray(response.data) ? response.data[0] : response.data
    if (!row) { lotteryState.value = false; return }
    lotteryData.value = row.format_lottery_numbers || null
    lotteryState.value = Boolean(lotteryData.value)
    lotteryGameNo.value = row.game_no
  } catch (_) {
    // 旧版同样把接口错误当成「未开奖」处理
    lotteryState.value = false
  }
}

/** 旧版 doRefresh：page_index 归 1，重新拉开奖与统计 */
async function doRefresh() {
  page.value = { ...page.value, page_index: 1 }
  await runAll()
}

/** 旧版 doInfinite：超过最后一页时提示，否则页码 +1 追加 */
async function doInfinite() {
  if (page.value.page_index + 1 > page.value.page_count) {
    errorMessage.value = '已经最后一页了'
    return
  }
  page.value = { ...page.value, page_index: page.value.page_index + 1 }
  loading.value = true
  try { await loadStatistic() } catch (error) { errorMessage.value = error.message || '加载失败' } finally { loading.value = false }
}

async function runAll() {
  loading.value = true
  errorMessage.value = ''
  try {
    await Promise.all([loadStatistic(), loadLottery()])
  } catch (error) {
    errorMessage.value = error.message || '加载失败'
  } finally {
    loading.value = false
  }
}

// 旧版 ngOnInit：page_index=1; getStatistic(); getLotteryData()
onMounted(() => { page.value = { ...page.value, page_index: 1 }; void runAll() })
</script>

<template>
  <div class="statistic-modal">
    <!-- 旧版 ion-header > ion-toolbar：左侧 arrow-back 返回按钮 + 标题「数据统计」 -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="emit('close')">
        <!-- 旧版 ion-icon[name=arrow-back]，md 模式渲染的是 Material arrow_back，用内联 SVG 复刻 -->
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">数据统计<span v-if="gameTypeLabel(game)"> · {{ gameTypeLabel(game) }}</span></h1>
      <button class="modal-refresh" type="button" aria-label="下拉刷新" @click="doRefresh">↻</button>
    </header>

    <div class="modal-content">
      <section class="ion-list">
        <!-- 旧版 ion-list-header.lists-header：期号 + 未开奖 / 开奖统计 -->
        <div class="list-header lists-header">
          <span class="game-no"> 第 <span>{{ headerGameNo }}</span>期 </span>
          <span v-if="!lotteryState" class="lottery-un"> 未开奖 </span>
          <span v-else class="lottery-ed"> 开奖统计 </span>
        </div>

        <div class="list-item">
          <table class="table">
            <tbody>
              <!-- 第一行：玩法名称（旧版首格固定是 " - "） -->
              <tr>
                <td> - </td>
                <td v-for="play in statistic.total" :key="`title-${play.play_id}`"> {{ play.play_title }} </td>
              </tr>
              <!-- 旧版 Ll：只有开奖后才有这一行，逐门显示两张牌 + N点 + 名次 -->
              <tr v-if="lotteryState && lotteryData">
                <td> 开奖： </td>
                <td v-for="(item, index) in (lotteryData.result || [])" :key="`lottery-${index}`">
                  <div class="lottery-ball">
                    <!-- 旧版直接拼 item，不补零 -->
                    <img v-if="!isOzbPj" v-for="(card, cardIndex) in (item.item || [])" :key="`image-${cardIndex}`" :src="`/legacy/images/game/paijiu/ball/${card}.png`" alt="" />
                    <span v-else class="ozbpj-number">{{ item.sum }}</span>
                  </div>
                  <div class="lottery-sum"> {{ item.sum }}点 </div>
                  <div v-if="!isOzbPj" class="lottery-rank"> {{ item.rank }} </div>
                </td>
              </tr>
              <!-- 个人行 -->
              <tr>
                <td> 个人： </td>
                <td v-for="play in statistic.member" :key="`member-${play.play_id}`">
                  <div class="user-bet-money"> {{ fmt2(play.bet_money) }} </div>
                  <div class="user-bet-money-win">
                    <span v-if="lotteryState" :class="signClass(play.bet_money_win)">{{ raw(play.bet_money_win) }}</span>
                    <span v-else>-</span>
                  </div>
                  <div class="user-bet-money-pl">
                    <span v-if="lotteryState" :class="signClass(play.bet_money_pl)"> {{ raw(play.bet_money_pl) }} </span>
                    <span v-else>-</span>
                  </div>
                </td>
              </tr>
              <!-- 总计行 -->
              <tr>
                <td> 总计： </td>
                <td v-for="play in statistic.total" :key="`total-${play.play_id}`">
                  <div class="user-bet-money"> {{ fmt2(play.bet_money) }} </div>
                  <div class="user-bet-money-win">
                    <span v-if="lotteryState" :class="signClass(play.bet_money_win)">{{ raw(play.bet_money_win) }}</span>
                    <span v-else>-</span>
                  </div>
                  <div class="user-bet-money-pl">
                    <span v-if="lotteryState" :class="signClass(play.bet_money_pl)"> {{ raw(play.bet_money_pl) }} </span>
                    <span v-else>-</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 旧版 Ul：个人汇总（ngIf statistic.member_total） -->
        <template v-if="statistic.member_total">
          <div class="list-header"> 个人汇总 </div>
          <div class="list-item">
            <div class="avatar"><img class="user-avatar" :src="avatarUrl(statistic.member_total.member_avatar)" alt="" @error="onAvatarError" /></div>
            <div class="user-bet">
              <div class="user-nickname"> {{ statistic.member_total.member_nickname }} </div>
              <div class="user-bet-money"> {{ fmt2(statistic.member_total.bet_money) }} </div>
              <div class="user-bet-money-win">
                <span v-if="lotteryState" :class="signClass(statistic.member_total.bet_money_win)">{{ fmt2(statistic.member_total.bet_money_win) }}</span>
                <span v-else>-</span>
              </div>
              <div class="user-bet-money-pl">
                <span v-if="lotteryState" :class="signClass(statistic.member_total.bet_money_pl)"> {{ fmt2(statistic.member_total.bet_money_pl) }} </span>
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </template>
      </section>

      <!-- 旧版第二个 ion-list：统计明细（按会员聚合，可分页追加） -->
      <section class="ion-list">
        <div class="list-header"> 统计明细 </div>
        <div v-for="row in statistic.lists" :key="row.member_id" class="list-item">
          <div class="avatar"><img class="user-avatar" :src="avatarUrl(row.member?.member_avatar)" alt="" @error="onAvatarError" /></div>
          <div class="user-bet">
            <div class="user-nickname"> {{ row.member?.member_nickname }} </div>
            <div class="user-bet-money"> {{ fmt2(row.bet_money) }} </div>
            <div class="user-bet-money-win">
              <span v-if="lotteryState" :class="signClass(row.bet_money_win)">{{ fmt2(row.bet_money_win) }}</span>
              <span v-else>-</span>
            </div>
            <div class="user-bet-money-pl">
              <span v-if="lotteryState" :class="signClass(row.bet_money_pl)"> {{ fmt2(row.bet_money_pl) }} </span>
              <span v-else>-</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 ion-infinite-scroll：仅在还有下一页时挂载 -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        {{ loading ? '正在加载' : '加载更多' }}
      </button>
      <p v-if="errorMessage" class="modal-notice">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<style scoped>
/* 旧版样式表 Pl 逐条还原；:host → .statistic-modal */
.statistic-modal{position:fixed;z-index:30;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
/* 旧版 ion-toolbar 没写 color，红底白字来自主题模块注入 :root 的
   --ion-toolbar-background: #f95454 / --ion-toolbar-color: #ffffff（见 base.css 注释）。
   md 模式工具栏高 56px；box-shadow 复刻 md 工具栏投影，
   position/z-index 保证阴影压在滚动内容之上 */
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back,.modal-refresh{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;font-size:22px;line-height:1;color:#fff;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
/* 旧版 ion-title：font-weight 500，不是加粗 */
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:#fff}
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch}
.ion-list{padding:0}
.list-header{display:flex;align-items:center;min-height:36px;padding:0 10px;color:#666;font-size:.9rem}
/* 旧版 .lists-header{justify-content:flex-start} */
.lists-header{justify-content:flex-start}
.list-header .game-no{margin-right:10px}
.list-header .lottery-un{color:#999}
.list-header .lottery-ed{color:red}
/* 旧版 :host ion-item{font-size:.8rem} */
.list-item{display:flex;align-items:center;padding:4px 10px;border-bottom:1px solid #eee;font-size:.8rem}
.list-item .avatar{width:40px;margin-right:8px}
.list-item .avatar .user-avatar{width:40px;height:40px;border-radius:50%;object-fit:cover}
.table{width:100%;text-align:center;border:1px solid #ccc;font-size:.8rem;border-collapse:collapse}
.table tr{border:1px solid #ccc;min-height:2rem;line-height:2rem}
.table tr td{border:1px solid #ccc;width:16.66%}
.user-bet{flex:1;display:flex;font-size:.8rem;line-height:2rem}
.user-bet .user-nickname{font-size:.8rem;line-height:2rem;width:25%;padding-left:5px}
.user-bet .user-bet-money{width:25%}
.user-bet .user-bet-money-win{width:25%}
.user-bet .user-bet-money-pl{width:25%}
.user-bet-money{color:#666}
.user-bet-money-win{color:#666}
.user-bet-money-pl{color:#666}
.win{color:red}
.lost{color:green}
.lottery-ball{margin-top:5px}
.lottery-ball img{width:45%}
.lottery-ball .ozbpj-number{display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;min-width:25px;height:25px;padding:0 6px;border:1px solid #8ed0ff;border-radius:50%;background:#1479d1;color:#fff;font-weight:700;font-size:14px;line-height:1}
.lottery-sum{color:red}
.lottery-rank{color:#5f9ea0}
.load-more{display:block;width:calc(100% - 20px);margin:10px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:0 10px 10px;color:#999;font-size:.8rem;text-align:center}
</style>
